/** @jsx jsx */
import React, { useEffect, useState, useCallback } from 'react';

import { css, jsx } from '@compiled/react';

import Button from '@atlaskit/button';
import Lozenge from '@atlaskit/lozenge';
import {
	Spotlight,
	SpotlightManager,
	SpotlightTarget,
	SpotlightTransition,
} from '@atlaskit/onboarding';
import Popup from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import { layers } from '@atlaskit/theme/constants';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';

import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { Link } from '@atlassian/jira-router';
import { useAccountId } from '@atlassian/jira-tenant-context-controller';
import hierarchyGif from './assets/hieararchy-animation.gif';
import InfoIcon from './assets/info-icon';

import messages from './messages';
import { getUserProperty, setUserProperty } from './utils';

type Props = {
	leftPosition?: number;
};

const PARENT_CHANGEBOARDING_UI_EVENT = 'parentFieldChangeboarding';

export const spotlightUrl =
	'https://community.atlassian.com/t5/Jira-Software-articles/Introducing-the-new-Parent-field-in-company-managed-projects/ba-p/2377758';

export const learnMoreUrl =
	'https://support.atlassian.com/jira-cloud-administration/docs/what-are-issue-types/#What-are-parent-and-child-issues';

export const Changeboarding = (props: Props) => {
	const { leftPosition } = props;
	// setting the default to true to avoid new/seen flicker in the long run.
	const [changeboardingDismissed, setChangeboardingDismissed] = useState<boolean>(true);
	const { formatMessage } = useIntl();

	const [isOpen, setIsOpen] = useState(false);

	const [isSpotlightActive, setIsSpotlightActive] = useState(false);
	const accountId = useAccountId();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (accountId != null) {
			getUserProperty(accountId).then((hasDismissed) => setChangeboardingDismissed(hasDismissed));
		}
	}, [accountId]);

	const onClickNewLozenge = useCallback(() => {
		setIsSpotlightActive(true);

		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'lozenge',
		});

		fireUIAnalytics(analyticsEvent, PARENT_CHANGEBOARDING_UI_EVENT);
	}, [setIsSpotlightActive, createAnalyticsEvent]);

	const onClickInfo = useCallback(() => {
		setIsOpen(!isOpen);

		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'infoButton',
		});

		fireUIAnalytics(analyticsEvent, PARENT_CHANGEBOARDING_UI_EVENT);
	}, [isOpen, setIsOpen, createAnalyticsEvent]);

	const onInfolinkClicked = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'learnMoreLink',
		});

		fireUIAnalytics(analyticsEvent, PARENT_CHANGEBOARDING_UI_EVENT);
	}, [createAnalyticsEvent]);

	const onDismiss = useCallback(() => {
		if (accountId != null) {
			setChangeboardingDismissed(true);
			setUserProperty(accountId);
		}
	}, [setChangeboardingDismissed, accountId]);

	const newLearnMore = useCallback(() => {
		setIsSpotlightActive(false);

		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'newLearnMoreLink',
		});

		fireUIAnalytics(analyticsEvent, PARENT_CHANGEBOARDING_UI_EVENT);
		onDismiss();
	}, [setIsSpotlightActive, onDismiss, createAnalyticsEvent]);

	const newDismiss = useCallback(() => {
		setIsSpotlightActive(false);

		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'okButton',
		});

		fireUIAnalytics(analyticsEvent, PARENT_CHANGEBOARDING_UI_EVENT);
		onDismiss();
	}, [setIsSpotlightActive, onDismiss, createAnalyticsEvent]);

	return changeboardingDismissed ? (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			shouldRenderToParent
			placement="right"
			zIndex={layers.tooltip()}
			content={({ setInitialFocusRef }) => (
				<Box xcss={popupContentStyles}>
					<p>{formatMessage(messages.infoMessage)}</p>
					<p>
						<Link
							ref={setInitialFocusRef}
							target="_blank"
							href={learnMoreUrl}
							onClick={() => onInfolinkClicked()}
						>
							{formatMessage(messages.infoLearnMore)}
						</Link>
					</p>
				</Box>
			)}
			trigger={(triggerProps) => (
				<Box
					xcss={[
						leftPosition
							? xcss({
									position: 'absolute',
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
									left: `${leftPosition}px`,
									top: '3px',
								})
							: undefined,
						infoButtonContainerStyles,
					]}
				>
					<Button
						{...triggerProps}
						appearance="subtle-link"
						data-ds--inline-message--button
						spacing="none"
						isSelected={isOpen}
						onClick={() => onClickInfo()}
						aria-label={formatMessage(messages.spotligthLearnMore)}
					>
						<InfoIcon />
					</Button>
				</Box>
			)}
		/>
	) : (
		<SpotlightManager blanketIsTinted={false}>
			<SpotlightTarget name="parent-field-new">
				<Box
					xcss={[
						leftPosition
							? xcss({
									position: 'absolute',
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
									left: `${leftPosition}px`,
									top: '3px',
								})
							: undefined,
						newButtonContainerStyles,
					]}
				>
					<Button spacing="none" appearance="subtle-link" onClick={() => onClickNewLozenge()}>
						<Lozenge
							appearance="new"
							// @ts-expect-error Property 'css' does not exist on type 'IntrinsicAttributes & LozengeProps'.ts(2322)
							// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
							css={newButtonLozengeStyles}
						>
							{formatMessage(messages.newLabel)}
						</Lozenge>
					</Button>
				</Box>
			</SpotlightTarget>

			<SpotlightTransition>
				{isSpotlightActive && (
					<Spotlight
						pulse={false}
						dialogPlacement="right middle"
						dialogWidth={350}
						image={hierarchyGif}
						actions={[
							{
								onClick: () => newDismiss(),
								text: formatMessage(messages.ok),
							},
							{
								onClick: () => newLearnMore(),
								text: formatMessage(messages.spotligthLearnMore),
								href: spotlightUrl,
								target: '_blank',
								appearance: 'subtle',
							},
						]}
						heading=""
						target="parent-field-new"
						key="parent-field-new"
						targetRadius={3}
					>
						{/* eslint-disable-next-line jira/react/no-empty-divs */}
						<div>
							<FormattedMessage
								{...messages.spotlightMessage}
								values={{
									strong: (chunks) => <strong>{chunks}</strong>,
								}}
							/>
						</div>
					</Spotlight>
				)}
			</SpotlightTransition>
		</SpotlightManager>
	);
};

const infoButtonContainerStyles = xcss({
	marginLeft: 'space.050',
	// eslint-disable-next-line @atlaskit/design-system/use-latest-xcss-syntax
	marginTop: '-1px',
	display: 'inline-block',
});

const newButtonLozengeStyles = css({
	lineHeight: '20px',
});

const newButtonContainerStyles = xcss({
	// eslint-disable-next-line @atlaskit/design-system/use-latest-xcss-syntax
	marginTop: '-3px',
	// eslint-disable-next-line @atlaskit/design-system/use-latest-xcss-syntax
	marginBottom: '-1px',
	marginLeft: 'space.050',
	marginRight: '0',
	display: 'inline-block',
});

const popupContentStyles = xcss({
	paddingTop: 'space.200',
	paddingBottom: 'space.200',
	paddingLeft: 'space.250',
	paddingRight: 'space.250',
	width: '300px',
});
